import { useMemo } from "react";
import { Form, Field } from "react-final-form";
import { useQueryClient } from "@tanstack/react-query";
import Icon from "components/Icon";
import { AdminSelectAdapter } from "components/select";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import type {
	CompetitionEditionRowResponse,
	CompetitionEditionUpsertRequest,
} from "libs/apiClient";
import { useToast } from "ui-kit";
import type { SelectOption } from "./competitionOptions";

type EditionFormValues = {
	season: SelectOption | null;
	groupLabel: string;
	phaseLabel: string;
	startDate: string;
	endDate: string;
	notes: string;
	isScoutVisible: boolean;
};

type EditionFormErrors = {
	season?: string;
	endDate?: string;
};

export type CompetitionEditionFormModalProps = {
	competitionId: number;
	hasGroups?: boolean;
	hasPhases?: boolean;
	edition?: CompetitionEditionRowResponse | null;
	onClose: () => void;
	onSaved: () => void;
};

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return "Something went wrong";
}

function validateEditionForm(values: EditionFormValues): EditionFormErrors {
	const errors: EditionFormErrors = {};
	if (values.season == null || values.season.value === "") {
		errors.season = "Season is required";
	}
	if (values.startDate !== "" && values.endDate !== "" && values.endDate < values.startDate) {
		errors.endDate = "The end date must be on or after the start date";
	}
	return errors;
}

export function CompetitionEditionFormModal({
	competitionId,
	hasGroups = false,
	hasPhases = false,
	edition,
	onClose,
	onSaved,
}: CompetitionEditionFormModalProps) {
	const queryClient = useQueryClient();
	const { addSuccessToast, addErrorToast } = useToast();
	const isEdit = edition?.id != null;

	const { data: seasonsResponse } = client.getSeasons.useQuery({ retry: false });
	const createMutation = client.postCompetitionEdition.useMutation();
	const updateMutation = client.putCompetitionEdition.useMutation();

	const seasonOptions = useMemo((): SelectOption[] => {
		const seasons = seasonsResponse?.data?.items ?? [];
		return seasons
			.filter((season) => season.id != null && season.name != null)
			.map((season) => ({
				value: String(season.id),
				label: season.name ?? String(season.id),
			}));
	}, [seasonsResponse]);

	const initialValues = useMemo((): EditionFormValues => {
		const seasonOption =
			edition?.seasonId != null
				? seasonOptions.find((option) => option.value === String(edition.seasonId)) ?? {
						value: String(edition.seasonId),
						label: edition.seasonName ?? String(edition.seasonId),
					}
				: null;

		return {
			season: seasonOption,
			groupLabel: edition?.groupLabel ?? "",
			phaseLabel: edition?.phaseLabel ?? "",
			startDate: edition?.startDate ?? "",
			endDate: edition?.endDate ?? "",
			notes: edition?.notes ?? "",
			isScoutVisible: edition?.isScoutVisible !== false,
		};
	}, [edition, seasonOptions]);

	const handleSubmit = async (values: EditionFormValues) => {
		if (values.season == null) {
			return;
		}

		const seasonId = Number(values.season.value);
		if (Number.isFinite(seasonId) === false || seasonId <= 0) {
			addErrorToast("Invalid season");
			return;
		}

		const body: CompetitionEditionUpsertRequest = {
			competitionId,
			seasonId,
			groupLabel: hasGroups && values.groupLabel.trim() !== "" ? values.groupLabel.trim() : null,
			phaseLabel: hasPhases && values.phaseLabel.trim() !== "" ? values.phaseLabel.trim() : null,
			isScoutVisible: values.isScoutVisible === true,
			startDate: values.startDate !== "" ? values.startDate : null,
			endDate: values.endDate !== "" ? values.endDate : null,
			notes: values.notes.trim() !== "" ? values.notes.trim() : null,
		};

		try {
			if (isEdit && edition?.id != null) {
				await updateMutation.mutateAsync({ id: edition.id, body });
				addSuccessToast("Season edition updated");
			} else {
				await createMutation.mutateAsync({ body });
				addSuccessToast("Season edition created");
			}

			await queryClient.invalidateQueries({ queryKey: ["getCompetitionEditions"] });
			await queryClient.invalidateQueries({ queryKey: ["getCompetitions"] });
			onSaved();
			onClose();
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		}
	};

	const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

	return (
		<div className="modal open">
			<span className="modal__backdrop" onClick={onClose} />
			<div className="modal__container modal__container--lg">
				<span className="modal__close" onClick={onClose}>
					<Icon icon="cross" />
				</span>
				<div className="modal__title">{isEdit ? "Edit season edition" : "Add season edition"}</div>
				<div className="modal__body">
					<div className="admin-creation-container">
						<div className="admin-creation-form">
							<Form
								onSubmit={handleSubmit}
								initialValues={initialValues}
								validate={validateEditionForm}
								keepDirtyOnReinitialize
								render={({ handleSubmit: submitForm, errors, touched, submitFailed }) => (
									<form onSubmit={submitForm}>
										<div className="form-row">
											<div className="field-group">
												<label className="required">Season</label>
												<Field name="season" component={AdminSelectAdapter} options={seasonOptions} />
												{(touched?.season || submitFailed) && errors?.season ? (
													<span className="form-error">{errors.season}</span>
												) : null}
											</div>

											{hasGroups ? (
												<div className="field-group">
													<label>Group</label>
													<Field name="groupLabel">
														{({ input }) => (
															<input {...input} className="form-control" maxLength={64} />
														)}
													</Field>
												</div>
											) : null}

											{hasPhases ? (
												<div className="field-group">
													<label>Phase</label>
													<Field name="phaseLabel">
														{({ input }) => (
															<input {...input} className="form-control" maxLength={64} />
														)}
													</Field>
												</div>
											) : null}
										</div>

										<div className="form-row">
											<div className="field-group">
												<label>Start date</label>
												<Field name="startDate">
													{({ input }) => <input {...input} type="date" className="form-control" />}
												</Field>
											</div>

											<div className="field-group">
												<label>End date</label>
												<Field name="endDate">
													{({ input }) => <input {...input} type="date" className="form-control" />}
												</Field>
												{(touched?.endDate || touched?.startDate || submitFailed) && errors?.endDate ? (
													<span className="form-error">{errors.endDate}</span>
												) : null}
											</div>
										</div>

										<div className="form-row">
											<div className="field-group w-100">
												<label>Notes</label>
												<Field name="notes">
													{({ input }) => (
														<textarea {...input} className="form-control textarea" maxLength={2000} />
													)}
												</Field>
											</div>
										</div>

										<div className="form-row competitions-form-toggles">
											<label className="competitions-checkbox">
												<Field name="isScoutVisible" type="checkbox" component="input" />
												<span>Scout visible</span>
											</label>
										</div>

										<div className="form-row competitions-form-actions">
											<button type="button" className="btn btn--cancel" onClick={onClose}>
												Cancel
											</button>
											<button type="submit" className="btn btn-light-blue" disabled={isSubmitting}>
												{isEdit ? "Save changes" : "Create edition"}
											</button>
										</div>
									</form>
								)}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default CompetitionEditionFormModal;
